/**
 * Study Amend Page
 *
 * Page for amending returned studies
 */

import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  Undo2,
  FileText,
  Image,
  Paperclip,
  Check,
} from 'lucide-react'

import { useStudyAmendInfo, useAmendStudy } from '@/application/studies'
import { Button } from '@/presentation/components/ui/Button'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/presentation/components/ui/Tabs'
import { toast } from '@/lib/toast'
import type { AmendStudyData } from '@/domain/study/types'

import { AmendMetadataTab } from '@/presentation/components/studies/AmendMetadataTab'
import { AmendInstancesTab } from '@/presentation/components/studies/AmendInstancesTab'
import { AmendAttachmentsTab } from '@/presentation/components/studies/AmendAttachmentsTab'
import { AmendConfirmModal } from '@/presentation/components/studies/AmendConfirmModal'

export function StudyAmendPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // State for changes
  const [metadata, setMetadata] = useState<AmendStudyData['metadata']>({})
  const [deleteInstanceIds, setDeleteInstanceIds] = useState<string[]>([])
  const [newInstances, setNewInstances] = useState<AmendStudyData['newInstances']>([])
  const [deleteAttachmentIds, setDeleteAttachmentIds] = useState<string[]>([])
  const [newAttachments, setNewAttachments] = useState<AmendStudyData['newAttachments']>([])
  const [activeTab, setActiveTab] = useState('metadata')
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // Queries
  const { data: amendInfo, isLoading, isError, error } = useStudyAmendInfo(id)
  const amendMutation = useAmendStudy()

  // Initialize metadata from study
  useEffect(() => {
    if (amendInfo?.study) {
      setMetadata({
        studyDescription: amendInfo.study.studyDescription || '',
        referringPhysician: amendInfo.study.referringPhysician || '',
        performingPhysician: amendInfo.study.performingPhysician || '',
        bodyPartExamined: amendInfo.study.bodyPartExamined || '',
      })
    }
  }, [amendInfo?.study])

  // Check if can amend
  useEffect(() => {
    if (amendInfo && !amendInfo.canAmend) {
      toast.error('Este estudio no puede ser enmendado')
      navigate(`/studies/${id}`)
    }
  }, [amendInfo, id, navigate])

  // Calculate if there are changes
  const hasChanges = useMemo(() => {
    if (!amendInfo) return false

    const metadataChanged =
      metadata?.studyDescription !== (amendInfo.study.studyDescription || '') ||
      metadata?.referringPhysician !== (amendInfo.study.referringPhysician || '') ||
      metadata?.performingPhysician !== (amendInfo.study.performingPhysician || '') ||
      metadata?.bodyPartExamined !== (amendInfo.study.bodyPartExamined || '')

    return (
      metadataChanged ||
      deleteInstanceIds.length > 0 ||
      (newInstances?.length || 0) > 0 ||
      deleteAttachmentIds.length > 0 ||
      (newAttachments?.length || 0) > 0
    )
  }, [amendInfo, metadata, deleteInstanceIds, newInstances, deleteAttachmentIds, newAttachments])

  // Summary counts
  const summary = useMemo(() => {
    let metadataCount = 0
    if (amendInfo) {
      if (metadata?.studyDescription !== (amendInfo.study.studyDescription || '')) metadataCount++
      if (metadata?.referringPhysician !== (amendInfo.study.referringPhysician || '')) metadataCount++
      if (metadata?.performingPhysician !== (amendInfo.study.performingPhysician || '')) metadataCount++
      if (metadata?.bodyPartExamined !== (amendInfo.study.bodyPartExamined || '')) metadataCount++
    }

    return {
      metadata: metadataCount,
      instancesDeleted: deleteInstanceIds.length,
      instancesAdded: newInstances?.length || 0,
      attachmentsDeleted: deleteAttachmentIds.length,
      attachmentsAdded: newAttachments?.length || 0,
    }
  }, [amendInfo, metadata, deleteInstanceIds, newInstances, deleteAttachmentIds, newAttachments])

  const handleSubmit = async (amendmentNote: string) => {
    if (!id || !hasChanges) return

    try {
      await amendMutation.mutateAsync({
        id,
        data: {
          amendmentNote,
          metadata,
          deleteInstanceIds: deleteInstanceIds.length > 0 ? deleteInstanceIds : undefined,
          newInstances: newInstances?.length ? newInstances : undefined,
          deleteAttachmentIds: deleteAttachmentIds.length > 0 ? deleteAttachmentIds : undefined,
          newAttachments: newAttachments?.length ? newAttachments : undefined,
        },
      })

      toast.success('Estudio enmendado y reasignado exitosamente')
      navigate(`/studies/${id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al enmendar el estudio')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--foreground-muted)]">Cargando informacion...</p>
        </div>
      </div>
    )
  }

  if (isError || !amendInfo) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-[var(--foreground)] mb-2">
          Error al cargar estudio
        </h2>
        <p className="text-[var(--foreground-muted)] mb-4">
          {error instanceof Error ? error.message : 'No se pudo cargar la informacion'}
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--foreground)]">
              Enmendar Estudio
            </h1>
            <p className="text-[var(--foreground-muted)]">
              {amendInfo.study.studyDescription || amendInfo.study.accessionNumber || 'Sin descripcion'}
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowConfirmModal(true)}
          disabled={!hasChanges || amendMutation.isPending}
        >
          {amendMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Guardar y Reasignar
            </>
          )}
        </Button>
      </div>

      {/* Return Info Alert */}
      <Card className="alert-warning border-0">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-[var(--color-warning)]/20">
              <Undo2 className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Motivo de Devolucion</h3>
              <p className="text-sm mt-1">
                {amendInfo.worklistItem.returnReason || 'Sin motivo especificado'}
              </p>
              {amendInfo.worklistItem.returnedAt && (
                <p className="text-xs opacity-80 mt-2">
                  Devuelto el: {new Date(amendInfo.worklistItem.returnedAt).toLocaleString('es-CO')}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="metadata" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Metadatos
            {summary.metadata > 0 && (
              <Badge variant="secondary" className="ml-1">{summary.metadata}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="instances" className="flex items-center gap-2">
            <Image className="h-4 w-4" />
            Imagenes
            {(summary.instancesDeleted > 0 || summary.instancesAdded > 0) && (
              <Badge variant="secondary" className="ml-1">
                {summary.instancesDeleted > 0 && `-${summary.instancesDeleted}`}
                {summary.instancesAdded > 0 && `+${summary.instancesAdded}`}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="attachments" className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Adjuntos
            {(summary.attachmentsDeleted > 0 || summary.attachmentsAdded > 0) && (
              <Badge variant="secondary" className="ml-1">
                {summary.attachmentsDeleted > 0 && `-${summary.attachmentsDeleted}`}
                {summary.attachmentsAdded > 0 && `+${summary.attachmentsAdded}`}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metadata" className="mt-4">
          <AmendMetadataTab
            study={amendInfo.study}
            metadata={metadata || {}}
            onChange={setMetadata}
          />
        </TabsContent>

        <TabsContent value="instances" className="mt-4">
          <AmendInstancesTab
            studyId={id!}
            instances={amendInfo.instances}
            deleteInstanceIds={deleteInstanceIds}
            onDeleteChange={setDeleteInstanceIds}
            newInstances={newInstances || []}
            onNewInstancesChange={setNewInstances}
          />
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <AmendAttachmentsTab
            studyId={id!}
            attachments={amendInfo.attachments}
            deleteAttachmentIds={deleteAttachmentIds}
            onDeleteChange={setDeleteAttachmentIds}
            newAttachments={newAttachments || []}
            onNewAttachmentsChange={setNewAttachments}
          />
        </TabsContent>
      </Tabs>

      {/* Summary Panel */}
      {hasChanges && (
        <Card className="alert-info border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Check className="h-5 w-5" />
                <div className="text-sm">
                  <span className="font-medium">Cambios pendientes:</span>
                  {summary.metadata > 0 && ` ${summary.metadata} campo(s) de metadatos`}
                  {summary.instancesDeleted > 0 && ` - ${summary.instancesDeleted} imagen(es) a eliminar`}
                  {summary.instancesAdded > 0 && ` - ${summary.instancesAdded} imagen(es) nueva(s)`}
                  {summary.attachmentsDeleted > 0 && ` - ${summary.attachmentsDeleted} adjunto(s) a eliminar`}
                  {summary.attachmentsAdded > 0 && ` - ${summary.attachmentsAdded} adjunto(s) nuevo(s)`}
                </div>
              </div>
              <Button size="sm" onClick={() => setShowConfirmModal(true)}>
                Guardar Cambios
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirm Modal */}
      <AmendConfirmModal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        summary={summary}
        onConfirm={handleSubmit}
        isLoading={amendMutation.isPending}
      />
    </div>
  )
}
